import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { PageErrorUi, Loading } from 'components';
import { useFetch, useMutation, useRetailer } from 'utils/hooks';
import { DEFAULT_TODOS } from './stubs';
import { getSteps, getVisibleSteps } from './utils';
import { getOnboardingStatus } from './FtuWizard/utils';

/**
 * @type {import("react").Context<{
 *  todos: typeof import("./stubs").DEFAULT_TODOS,
 *  steps: import('utils/types').StepperStep[],
 *  visibleSteps: import('utils/types').StepperStep[],
 *  onCreateTasks: (payload: { todos: (keyof typeof import("./stubs").DEFAULT_TODOS)[]}) => void,
 *  onDeleteTasks: (payload: { todos: (keyof typeof import("./stubs").DEFAULT_TODOS)[]}) => void
 *  onMarkTodoDone: (id: keyof typeof import("./stubs").DEFAULT_TODOS) => void
 * }>}
 */
export const StoreOnboardingContext = createContext();

export const useStoreOnboarding = () => {
  const context = useContext(StoreOnboardingContext);
  if (!context) {
    throw new Error(
      'useStoreOnboarding must be used within a StoreOnboardingProvider'
    );
  }
  return context;
};

export const StoreOnboardingProvider = ({ children = null }) => {
  const { isAdvancedMode, getStoreProperty } = useRetailer();

  const [todos, setTodos] = useState(DEFAULT_TODOS);
  const [steps, setSteps] = useState([]);
  const [visibleSteps, setVisibleSteps] = useState([]);

  const isAdvancedSupported = getStoreProperty('is_enhanced_supported', false);

  useEffect(() => {
    setSteps(getSteps(isAdvancedMode, isAdvancedSupported));
    setVisibleSteps(getVisibleSteps(isAdvancedMode, isAdvancedSupported));
  }, [isAdvancedMode, isAdvancedSupported]);

  const updateTodos = useCallback(
    todos => {
      const { isStarted } = getOnboardingStatus(
        todos,
        isAdvancedMode,
        isAdvancedSupported
      );
      setTodos({ START: isStarted, ...todos });
    },
    [isAdvancedMode, isAdvancedSupported]
  );

  const { isError, isLoading, fetchData } = useFetch('todos.root', {
    errorMessage: 'Failed to load the data. Please try refreshing the page.',
    onSuccess: updateTodos
  });

  const { mutate: onUpdateTasks } = useMutation('todos.root', 'PUT', {
    onValidate: payload => !!payload && !_isEmpty(payload),
    onSuccess: updateTodos
  });

  const { mutate: onCreateTasks } = useMutation('todos.root', 'POST', {
    onValidate: payload => !_isEmpty(_get(payload, 'todos', [])),
    onSuccess: updateTodos
  });

  const { mutate: onDeleteTasks } = useMutation('todos.root', 'DELETE', {
    onValidate: payload => !_isEmpty(_get(payload, 'todos', [])),
    onSuccess: updateTodos
  });

  const onMarkTodoDone = useCallback(
    id => {
      const isDone = _get(todos, id, false);
      if (isDone) return;
      onUpdateTasks({ [id]: true });
    },
    [todos, onUpdateTasks]
  );

  return (
    <StoreOnboardingContext.Provider
      value={{
        todos,
        steps,
        visibleSteps,
        onCreateTasks,
        onDeleteTasks,
        onMarkTodoDone
      }}
    >
      <PageErrorUi isError={isError} onReload={fetchData}>
        <Loading loading={isLoading}>{children}</Loading>
      </PageErrorUi>
    </StoreOnboardingContext.Provider>
  );
};
